import React, { useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import TilePalette from '../components/PracticePlanner/TilePalette'
import PracticeSession from '../components/PracticePlanner/PracticeSession'
import PracticeDownload from '../components/PracticePlanner/PracticeDownload'
import PracticeSessionManager from '../components/PracticePlanner/PracticeSessionManager'
import AICoach from '../components/PracticePlanner/AICoach'
import NavigationHeader from '../components/NavigationHeader'
import usePracticeStore from '../stores/practiceStore'
import { loadPracticeSession } from '../services/practiceSessionsApi'
import { TileType } from '../components/PracticePlanner/TilePalette'
import { Trash2 } from 'lucide-react'

const PracticePlannerPage = () => {
  const [searchParams] = useSearchParams()
  const { tiles, clearTiles, clearNotes, resetSessionName, setTiles, setNotes, setSessionName, setOriginalLoadedSession } = usePracticeStore()

  const handleClear = () => {
    if (confirm('Clear all practice tiles and notes? Any unsaved progress will be lost.')) {
      clearTiles()
      clearNotes()
      resetSessionName()
    }
  }

  // Mock API call - generates practice tiles based on prompt
  const mockGeneratePractice = useCallback(async (userPrompt) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500))
    
    // Parse the prompt to extract information
    const lowerPrompt = userPrompt.toLowerCase()
    const durationMatch = userPrompt.match(/(\d+)\s*minute/i)
    const totalDuration = durationMatch ? parseInt(durationMatch[1]) : 90
    
    // Determine focus areas
    const hasDefense = lowerPrompt.includes('defense') || lowerPrompt.includes('defensive')
    const hasOffense = lowerPrompt.includes('offense') || lowerPrompt.includes('offensive') || lowerPrompt.includes('attacking')
    const hasPositional = lowerPrompt.includes('positional') || lowerPrompt.includes('position')
    const hasFitness = lowerPrompt.includes('fitness') || lowerPrompt.includes('conditioning')
    const hasPassing = lowerPrompt.includes('passing')
    const hasShooting = lowerPrompt.includes('shooting')
    
    // Generate tiles based on prompt
    const generatedTiles = []
    let currentTime = 0
    const timestamp = Date.now()
    
    // Warm-up (always start with this)
    generatedTiles.push({
      id: `ai-${timestamp}-0`,
      type: TileType.FITNESS,
      title: 'Warm-up',
      description: 'Dynamic stretching and light jogging to prepare the body for practice',
      duration: 10
    })
    currentTime += 10
    
    // Defense-focused activities
    if (hasDefense || hasPositional) {
      generatedTiles.push({
        id: `ai-${timestamp}-1`,
        type: TileType.DRILL,
        title: 'Defensive Shape Drill',
        description: 'Work on maintaining defensive shape and communication. Focus on positioning and spacing.',
        duration: 15
      })
      currentTime += 15
      
      if (hasPositional) {
        generatedTiles.push({
          id: `ai-${timestamp}-2`,
          type: TileType.DRILL,
          title: 'Positional Awareness Game',
          description: 'Small-sided game emphasizing positional responsibilities and spatial awareness.',
          duration: 20
        })
        currentTime += 20
      }
    }
    
    // Passing or technical work
    if (hasPassing && !hasDefense) {
      generatedTiles.push({
        id: `ai-${timestamp}-3`,
        type: TileType.DRILL,
        title: 'Passing Patterns',
        description: 'Work on passing accuracy, first touch, and movement off the ball.',
        duration: 15
      })
      currentTime += 15
    }
    
    // Shooting work
    if (hasShooting) {
      generatedTiles.push({
        id: `ai-${timestamp}-4`,
        type: TileType.DRILL,
        title: 'Finishing Drill',
        description: 'Practice shooting from various angles and situations. Focus on technique and accuracy.',
        duration: 15
      })
      currentTime += 15
    }
    
    // Scrimmage (always include if there's time)
    const remainingTime = totalDuration - currentTime
    if (remainingTime >= 20) {
      generatedTiles.push({
        id: `ai-${timestamp}-5`,
        type: TileType.SCRIMMAGE,
        title: 'Scrimmage',
        description: 'Apply learned concepts in a game-like situation. Coach provides feedback during play.',
        duration: Math.min(remainingTime - 5, 30) // Max 30 minutes, leave 5 for speaking points
      })
      currentTime += Math.min(remainingTime - 5, 30)
    }
    
    // Speaking points (always end with this)
    if (currentTime < totalDuration) {
      generatedTiles.push({
        id: `ai-${timestamp}-6`,
        type: TileType.SPEAKING_POINTS,
        title: 'Post-Practice Discussion',
        description: 'Review key points from practice. Discuss what went well and areas for improvement.',
        duration: Math.min(totalDuration - currentTime, 10)
      })
    }
    
    // If we still have time and haven't filled it, add fitness
    if (currentTime < totalDuration && hasFitness) {
      const fitnessTime = totalDuration - currentTime - 5 // Leave room for speaking points
      if (fitnessTime > 5) {
        generatedTiles.splice(-1, 0, {
          id: `ai-${timestamp}-7`,
          type: TileType.FITNESS,
          title: 'Conditioning',
          description: 'Endurance and fitness work to build stamina.',
          duration: fitnessTime
        })
      }
    }
    
    return generatedTiles
  }, [])

  // Load session from query parameter if present
  useEffect(() => {
    const loadSessionId = searchParams.get('load')
    if (loadSessionId) {
      loadPracticeSession(loadSessionId)
        .then(session => {
          setTiles(session.tiles || [])
          setNotes(session.notes || '')
          setSessionName(session.name)
          setOriginalLoadedSession({ id: session.id, name: session.name })
        })
        .catch(error => {
          console.error('Error loading session:', error)
          alert('Failed to load practice session: ' + error.message)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="h-screen w-screen flex flex-col bg-gray-50">
        {/* Navigation Header */}
        <NavigationHeader />

        {/* Page Actions Toolbar */}
        <div className="bg-white border-b border-gray-200 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-end space-x-2">
            <PracticeSessionManager />
            <PracticeDownload />
            <button
              onClick={handleClear}
              className="flex items-center justify-center space-x-1 w-32 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
              title="Clear all practice tiles and notes"
            >
              <Trash2 size={14} />
              <span>Clear</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex min-h-0">
          {/* Left Sidebar - Tile Palette */}
          <aside className="w-64 bg-white shadow-sm border-r border-gray-200 p-4 overflow-y-auto">
            <TilePalette />
          </aside>

          {/* Center - Practice Session */}
          <main className="flex-1 flex flex-col min-h-0 overflow-y-auto border-r border-gray-200">
            <div className="flex-1 p-6">
              <PracticeSession />
            </div>
          </main>

          {/* Right Sidebar - AI Coach */}
          <aside className="w-80 bg-white shadow-sm border-l border-gray-200 overflow-hidden">
            <AICoach
              mode="practice"
              title="AI Coach"
              description="Describe the practice you need, and AI will design it for you."
              labelText="Practice Request"
              buttonText="Generate Practice"
              placeholder="e.g., I need a 90 minute practice for U12 boys that focuses on defense and positional awareness"
              examplePrompts={[
                '• "90 minute practice for U12 focusing on passing and movement"',
                '• "60 minute defensive training session"',
                '• "U14 practice with fitness and shooting drills"',
                '• "45 minute positional awareness session"'
              ]}
              onGenerate={mockGeneratePractice}
              getExistingItems={() => tiles}
              clearItems={clearTiles}
              setItems={setTiles}
              confirmationMessage="This will replace all existing practice tiles with the new AI-generated practice. Are you sure you want to continue?"
            />
          </aside>
        </div>
      </div>
    </DndProvider>
  )
}

export default PracticePlannerPage

